import { ChatbotApiClient } from "./api";
import {
  ChatbotBootstrapResponse,
  ChatbotCourseAsset,
  ChatbotMessage,
  TraingeChatbotInitOptions,
} from "./types";
import {
  CourseAssetMap,
  createCourseAssetMap,
  findCourseAssetByHref,
  getCourseAssetUrl,
  mergeCourseAssets,
} from "./courseAssets";
import { shouldSubmitComposerOnKeydown } from "./composerKeys";
import { chooseSupportedAudioMimeType } from "./audioMimeTypes";
import { widgetStyles } from "./ui/styles";
import { ChatbotFeedbackAction, recordChatbotFeedback } from "./feedback";

const FIRST_RESPONSE_TIMEOUT_MS = 60_000;
const FIRST_RESPONSE_TIMEOUT_MESSAGE = "目前無法回應您的問題，請稍後再試。";

export class TraingeChatbotRuntime {
  private client: ChatbotApiClient;
  private root: HTMLDivElement;
  private messagesEl: HTMLDivElement;
  private inputEl: HTMLTextAreaElement;
  private sendButton: HTMLButtonElement;
  private recordButton: HTMLButtonElement;
  private config: ChatbotBootstrapResponse | null = null;
  private messages: ChatbotMessage[] = [];
  private courseAssets: ChatbotCourseAsset[] = [];
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private recordTimer: number | null = null;
  private audioContext: AudioContext | null = null;
  private audioSource: MediaStreamAudioSourceNode | null = null;
  private silenceFrame: number | null = null;
  private lastVoiceAt = 0;
  private isBusy = false;

  constructor(private options: TraingeChatbotInitOptions) {
    this.client = new ChatbotApiClient(options);
    this.root = document.createElement("div");
    this.root.className = "trainge-chatbot-root";
    this.root.dataset.open = "false";
    if (options.brandId?.trim()) {
      this.root.dataset.brandId = options.brandId.trim();
    }
    this.messagesEl = document.createElement("div");
    this.inputEl = document.createElement("textarea");
    this.sendButton = document.createElement("button");
    this.recordButton = document.createElement("button");
  }

  async mount() {
    this.injectStyles();
    this.renderShell();
    document.body.appendChild(this.root);
    this.config = await this.client.bootstrap();
    this.root.querySelector(".trainge-chatbot-title")!.textContent = this.config.displayName;
    this.inputEl.maxLength = this.config.limits.maxUserInputChars;
    this.recordButton.hidden = !this.isVoiceInputSupported();
    this.recordButton.disabled = !this.isVoiceInputSupported();

    if (this.config.welcomeMessage) {
      this.addMessage("assistant", this.config.welcomeMessage);
    }
  }

  unmount() {
    this.root.remove();
  }

  private injectStyles() {
    if (document.getElementById("trainge-chatbot-styles")) return;
    const style = document.createElement("style");
    style.id = "trainge-chatbot-styles";
    style.textContent = widgetStyles;
    document.head.appendChild(style);
  }

  private renderShell() {
    const launcher = document.createElement("button");
    launcher.className = "trainge-chatbot-button";
    launcher.type = "button";
    launcher.title = "Open chat";
    launcher.setAttribute("aria-label", "Open Trainge chatbot");
    launcher.appendChild(createLauncherIcon());
    launcher.addEventListener("click", () => {
      this.root.dataset.open = "true";
      this.inputEl.focus();
    });

    const panel = document.createElement("section");
    panel.className = "trainge-chatbot-panel";
    panel.setAttribute("aria-label", "Trainge chatbot");
    panel.innerHTML = `
      <header class="trainge-chatbot-header">
        <div class="trainge-chatbot-title">Trainge Assistant</div>
        <button class="trainge-chatbot-close" type="button" title="Close">×</button>
      </header>
    `;

    this.messagesEl.className = "trainge-chatbot-messages";
    this.inputEl.className = "trainge-chatbot-input";
    this.inputEl.placeholder = "有什麼可以幫您的?";
    this.inputEl.rows = 1;
    this.inputEl.addEventListener("keydown", (event) => {
      if (shouldSubmitComposerOnKeydown(event)) {
        event.preventDefault();
        void this.submit();
      }
    });
    this.inputEl.addEventListener("input", () => this.resizeInput());

    this.recordButton.className = "trainge-chatbot-icon-button";
    this.recordButton.type = "button";
    this.recordButton.title = "語音輸入";
    this.recordButton.setAttribute("aria-label", "語音輸入");
    this.recordButton.appendChild(createMicrophoneIcon());
    this.recordButton.addEventListener("click", () => void this.toggleRecording());

    this.sendButton.className = "trainge-chatbot-send";
    this.sendButton.type = "button";
    this.sendButton.title = "送出";
    this.sendButton.setAttribute("aria-label", "送出");
    this.sendButton.appendChild(createSendIcon());
    this.sendButton.addEventListener("click", () => void this.submit());

    const composer = document.createElement("form");
    composer.className = "trainge-chatbot-composer";
    composer.addEventListener("submit", (event) => {
      event.preventDefault();
      void this.submit();
    });
    const inputWrap = document.createElement("div");
    inputWrap.className = "trainge-chatbot-input-wrap";
    inputWrap.append(this.inputEl, this.recordButton, this.sendButton);
    composer.appendChild(inputWrap);

    panel.append(this.messagesEl, composer);
    panel.querySelector(".trainge-chatbot-close")!.addEventListener("click", () => {
      this.root.dataset.open = "false";
    });
    this.root.append(panel, launcher);
  }

  private addMessage(role: ChatbotMessage["role"], content: string) {
    this.messages.push({ role, content });
    const item = document.createElement("div");
    item.className = "trainge-chatbot-message";
    item.dataset.role = role;
    this.setMessageContent(item, role, content);
    this.messagesEl.appendChild(item);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
    return item;
  }

  private setMessageContent(
    element: HTMLElement,
    role: ChatbotMessage["role"],
    content: string,
    courseAssets: ChatbotCourseAsset[] = []
  ) {
    if (role === "assistant") {
      renderMarkdown(element, content, createCourseAssetMap(courseAssets));
      return;
    }

    element.textContent = content;
  }

  private async submit() {
    const message = this.inputEl.value.trim();
    await this.submitMessage(message);
  }

  private async submitMessage(message: string) {
    if (!message || !this.config) return;

    this.inputEl.value = "";
    this.resizeInput();
    this.setBusy(true);
    this.addMessage("user", message);
    const assistantEl = this.addMessage("assistant", "");
    this.setAssistantPending(assistantEl);
    let assistantText = "";
    let timedOut = false;
    let timeoutId: number | null = null;
    const controller = new AbortController();
    const firstResponseTimeout = new Promise<never>((_, reject) => {
      timeoutId = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
        reject(new Error(FIRST_RESPONSE_TIMEOUT_MESSAGE));
      }, FIRST_RESPONSE_TIMEOUT_MS);
    });

    try {
      await Promise.race([
        this.client.streamChat({
          message,
          signal: controller.signal,
          onCourseAssets: (courses) => {
            if (timedOut) return;
            this.courseAssets = mergeCourseAssets(this.courseAssets, courses);
            if (!assistantText) return;
            this.setMessageContent(assistantEl, "assistant", assistantText, this.courseAssets);
          },
          onToken: (token) => {
            if (timedOut) return;
            if (timeoutId !== null) {
              window.clearTimeout(timeoutId);
              timeoutId = null;
            }
            assistantText += token;
            this.clearAssistantPending(assistantEl);
            this.setMessageContent(assistantEl, "assistant", assistantText, this.courseAssets);
            this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
          },
          onAssistantMessageId: (id) => {
            if (timedOut) return;
            this.renderFeedbackControls(assistantEl, id);
            this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
          },
        }),
        firstResponseTimeout,
      ]);
    } catch (error) {
      this.clearAssistantPending(assistantEl);
      this.setMessageContent(
        assistantEl,
        "assistant",
        timedOut
          ? FIRST_RESPONSE_TIMEOUT_MESSAGE
          : error instanceof Error
            ? error.message
            : "回覆失敗，請稍後再試。"
      );
    } finally {
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      this.setBusy(false);
      if (this.root.dataset.open === "true") {
        this.inputEl.focus({ preventScroll: true });
      }
    }
  }

  private setAssistantPending(element: HTMLElement) {
    element.dataset.pending = "true";
    element.innerHTML = `
      <span class="trainge-chatbot-progress" role="status" aria-label="回覆產生中">
        <span class="trainge-chatbot-progress-ring" aria-hidden="true"></span>
      </span>
    `;
  }

  private clearAssistantPending(element: HTMLElement) {
    delete element.dataset.pending;
  }

  private renderFeedbackControls(element: HTMLElement, messageId: string) {
    if (!messageId || element.querySelector(".trainge-chatbot-feedback")) return;

    const feedback = document.createElement("div");
    feedback.className = "trainge-chatbot-feedback";

    const likeButton = createFeedbackButton("like");
    const dislikeButton = createFeedbackButton("dislike");

    const handleFeedback = (action: ChatbotFeedbackAction) => {
      if (feedback.dataset.feedbackSelected === action) return;

      feedback.dataset.feedbackSelected = action;
      feedback.dataset.feedbackState = "sending";
      likeButton.disabled = action === "like";
      dislikeButton.disabled = action === "dislike";
      void recordChatbotFeedback(action, messageId)
        .then(() => {
          feedback.dataset.feedbackState = "sent";
        })
        .catch(() => {
          feedback.dataset.feedbackState = "error";
        });
    };

    likeButton.addEventListener("click", () => handleFeedback("like"));
    dislikeButton.addEventListener("click", () => handleFeedback("dislike"));
    feedback.append(likeButton, dislikeButton);
    element.appendChild(feedback);
  }

  private setBusy(isBusy: boolean) {
    if (isBusy && this.mediaRecorder?.state === "recording") {
      this.mediaRecorder.stop();
    }
    this.isBusy = isBusy;
    this.sendButton.disabled = isBusy;
    this.inputEl.disabled = isBusy;
    this.recordButton.disabled = isBusy || !this.isVoiceInputSupported();
  }

  private resizeInput() {
    const computed = window.getComputedStyle(this.inputEl);
    const lineHeight = Number.parseFloat(computed.lineHeight) || 20;
    const paddingTop = Number.parseFloat(computed.paddingTop) || 0;
    const paddingBottom = Number.parseFloat(computed.paddingBottom) || 0;
    const borderTop = Number.parseFloat(computed.borderTopWidth) || 0;
    const borderBottom = Number.parseFloat(computed.borderBottomWidth) || 0;
    const maxHeight = lineHeight * 4 + paddingTop + paddingBottom + borderTop + borderBottom;

    this.inputEl.style.height = "auto";
    const nextHeight = Math.min(this.inputEl.scrollHeight, maxHeight);
    this.inputEl.style.height = `${nextHeight}px`;
    this.inputEl.style.overflowY = this.inputEl.scrollHeight > maxHeight ? "auto" : "hidden";
  }

  private isVoiceInputSupported() {
    return Boolean(
      this.config?.features.voiceInput &&
      typeof navigator.mediaDevices?.getUserMedia === "function" &&
      typeof MediaRecorder !== "undefined" &&
      chooseSupportedAudioMimeType() !== ""
    );
  }

  private async toggleRecording() {
    if (this.mediaRecorder?.state === "recording") {
      this.mediaRecorder.stop();
      return;
    }

    if (this.isBusy || !this.isVoiceInputSupported()) {
      return;
    }

    const config = this.config;
    if (!config) return;

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.chunks = [];
    const mimeType = chooseSupportedAudioMimeType();
    let mediaRecorder: MediaRecorder;
    try {
      mediaRecorder = new MediaRecorder(stream, { mimeType });
    } catch (error) {
      stream.getTracks().forEach((track) => track.stop());
      this.addMessage(
        "assistant",
        error instanceof Error ? error.message : "此瀏覽器不支援語音錄製。"
      );
      return;
    }
    this.mediaRecorder = mediaRecorder;
    mediaRecorder.addEventListener("dataavailable", (event) => {
      if (event.data.size > 0) this.chunks.push(event.data);
    });
    mediaRecorder.addEventListener("stop", () => {
      stream.getTracks().forEach((track) => track.stop());
      this.stopSilenceDetection();
      this.recordButton.dataset.recording = "false";
      if (this.recordTimer) window.clearTimeout(this.recordTimer);
      void this.finishRecording();
    });
    this.recordButton.dataset.recording = "true";
    mediaRecorder.start();
    this.startSilenceDetection(stream);
    const maxAudioDurationMs = config.limits.maxAudioDurationSeconds * 1000;
    this.recordTimer = window.setTimeout(() => {
      if (this.mediaRecorder?.state === "recording") this.mediaRecorder.stop();
    }, maxAudioDurationMs);
  }

  private startSilenceDetection(stream: MediaStream) {
    this.stopSilenceDetection();

    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) return;

    this.audioContext = new AudioContextCtor();
    const analyser = this.audioContext.createAnalyser();
    analyser.fftSize = 1024;
    this.audioSource = this.audioContext.createMediaStreamSource(stream);
    this.audioSource.connect(analyser);

    const samples = new Uint8Array(analyser.fftSize);
    const silenceMs = 3000;
    const threshold = 0.015;
    this.lastVoiceAt = Date.now();

    const detect = () => {
      if (this.mediaRecorder?.state !== "recording") return;

      analyser.getByteTimeDomainData(samples);
      let sum = 0;
      for (let index = 0; index < samples.length; index += 1) {
        const sample = samples[index];
        const normalized = (sample - 128) / 128;
        sum += normalized * normalized;
      }
      const volume = Math.sqrt(sum / samples.length);
      const now = Date.now();

      if (volume > threshold) {
        this.lastVoiceAt = now;
      } else if (now - this.lastVoiceAt >= silenceMs) {
        this.mediaRecorder.stop();
        return;
      }

      this.silenceFrame = window.requestAnimationFrame(detect);
    };

    this.silenceFrame = window.requestAnimationFrame(detect);
  }

  private stopSilenceDetection() {
    if (this.silenceFrame != null) {
      window.cancelAnimationFrame(this.silenceFrame);
      this.silenceFrame = null;
    }
    this.audioSource?.disconnect();
    this.audioSource = null;
    void this.audioContext?.close();
    this.audioContext = null;
  }

  private async finishRecording() {
    const mimeType =
      this.mediaRecorder?.mimeType || this.chunks.find((chunk) => chunk.type)?.type || "audio/webm";
    const audio = new Blob(this.chunks, { type: mimeType });
    if (!audio.size || this.isBusy || !this.isVoiceInputSupported()) return;

    this.recordButton.disabled = true;
    try {
      const result = await this.client.transcribe(audio);
      const transcript = result.text.trim();
      if (transcript && !this.isBusy) {
        await this.submitMessage(transcript);
      }
    } catch (error) {
      this.addMessage("assistant", error instanceof Error ? error.message : "語音辨識失敗。");
    } finally {
      this.recordButton.disabled = this.isBusy || !this.isVoiceInputSupported();
    }
  }
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}

type MarkdownRenderContext = {
  courseAssetMap: CourseAssetMap;
  renderedCourseUrls: Set<string>;
};

function renderMarkdown(target: HTMLElement, markdown: string, courseAssetMap: CourseAssetMap) {
  target.textContent = "";
  const context: MarkdownRenderContext = {
    courseAssetMap,
    renderedCourseUrls: new Set(),
  };

  const lines = markdown.split(/\r?\n/);
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (!line.trim()) {
      index += 1;
      continue;
    }

    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      const level = heading[1].length;
      const element = document.createElement(`h${level + 2}`);
      appendInlineMarkdown(element, heading[2], context);
      target.appendChild(element);
      index += 1;
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const list = document.createElement("ul");
      while (index < lines.length && /^\s*[-*]\s+/.test(lines[index])) {
        const item = document.createElement("li");
        appendInlineMarkdown(item, lines[index].replace(/^\s*[-*]\s+/, ""), context);
        list.appendChild(item);
        index += 1;
      }
      target.appendChild(list);
      continue;
    }

    if (/^\s*\d+[.)]\s+/.test(line)) {
      const list = document.createElement("ol");
      while (index < lines.length && /^\s*\d+[.)]\s+/.test(lines[index])) {
        const item = document.createElement("li");
        const orderedMatch = lines[index].match(/^\s*(\d+)[.)]\s+(.+)$/);
        if (orderedMatch) {
          item.value = Number(orderedMatch[1]);
          appendInlineMarkdown(item, orderedMatch[2], context);
        }
        list.appendChild(item);
        index += 1;
      }
      target.appendChild(list);
      continue;
    }

    const paragraphLines: string[] = [];
    while (
      index < lines.length &&
      lines[index].trim() &&
      !/^(#{1,3})\s+/.test(lines[index]) &&
      !/^\s*[-*]\s+/.test(lines[index]) &&
      !/^\s*\d+[.)]\s+/.test(lines[index])
    ) {
      paragraphLines.push(lines[index]);
      index += 1;
    }

    const paragraph = document.createElement("p");
    appendInlineMarkdown(paragraph, paragraphLines.join("\n"), context);
    target.appendChild(paragraph);
  }
}

function appendInlineMarkdown(parent: HTMLElement, text: string, context: MarkdownRenderContext) {
  const pattern = /(\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]+)\))/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text))) {
    if (match.index > cursor) {
      appendTextWithBreaks(parent, text.slice(cursor, match.index));
    }

    if (match[2]) {
      const strong = document.createElement("strong");
      appendInlineMarkdown(strong, match[2], context);
      parent.appendChild(strong);
    } else if (match[3]) {
      const code = document.createElement("code");
      code.textContent = match[3];
      parent.appendChild(code);
    } else if (match[4] && match[5]) {
      const link = document.createElement("a");
      link.textContent = match[4];
      link.href = match[5];
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      parent.appendChild(link);

      const asset = findCourseAssetByHref(context.courseAssetMap, match[5]);
      const assetUrl = asset ? getCourseAssetUrl(asset) : "";
      if (asset && assetUrl && !context.renderedCourseUrls.has(assetUrl)) {
        context.renderedCourseUrls.add(assetUrl);
        parent.appendChild(createCourseImage(asset));
      }
    }

    cursor = match.index + match[0].length;
  }

  if (cursor < text.length) {
    appendTextWithBreaks(parent, text.slice(cursor));
  }
}

function createCourseImage(asset: ChatbotCourseAsset) {
  const wrapper = document.createElement("span");
  wrapper.className = "trainge-chatbot-course-image-wrap";

  const image = document.createElement("img");
  image.className = "trainge-chatbot-course-image";
  image.src = asset.thumbnail_url;
  image.alt = asset.name ? `${asset.name} 圖片` : "Trainge 圖片";
  image.loading = "lazy";
  image.decoding = "async";

  wrapper.appendChild(image);
  return wrapper;
}

function appendTextWithBreaks(parent: HTMLElement, text: string) {
  const parts = text.split("\n");
  parts.forEach((part, index) => {
    if (index > 0) parent.appendChild(document.createElement("br"));
    if (part) parent.appendChild(document.createTextNode(part));
  });
}

function createLauncherIcon() {
  const namespace = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(namespace, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");

  const paths = [
    "M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z",
    "M8 9h8",
    "M8 13h5",
  ];

  paths.forEach((pathData) => {
    const path = document.createElementNS(namespace, "path");
    path.setAttribute("d", pathData);
    svg.appendChild(path);
  });

  return svg;
}

function createMicrophoneIcon() {
  const namespace = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(namespace, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");

  const paths = [
    "M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z",
    "M19 10v2a7 7 0 0 1-14 0v-2",
    "M12 19v3",
    "M8 22h8",
  ];

  paths.forEach((pathData) => {
    const path = document.createElementNS(namespace, "path");
    path.setAttribute("d", pathData);
    svg.appendChild(path);
  });

  return svg;
}

function createSendIcon() {
  const namespace = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(namespace, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");

  const path = document.createElementNS(namespace, "path");
  path.setAttribute("d", "M22 2 11 13");
  svg.appendChild(path);

  const secondPath = document.createElementNS(namespace, "path");
  secondPath.setAttribute("d", "m22 2-7 20-4-9-9-4 20-7z");
  svg.appendChild(secondPath);

  return svg;
}

function createFeedbackButton(action: ChatbotFeedbackAction) {
  const button = document.createElement("button");
  button.className = "trainge-chatbot-feedback-button";
  button.type = "button";
  button.dataset.feedbackAction = action;
  button.title = action === "like" ? "讚" : "倒讚";
  button.setAttribute("aria-label", action === "like" ? "讚" : "倒讚");
  button.appendChild(createThumbIcon(action));
  return button;
}

function createThumbIcon(action: ChatbotFeedbackAction) {
  const namespace = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(namespace, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");

  const paths = [
    "M7 10v12",
    "M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z",
  ];

  paths.forEach((pathData) => {
    const path = document.createElementNS(namespace, "path");
    path.setAttribute("d", pathData);
    svg.appendChild(path);
  });

  if (action === "dislike") {
    svg.style.transform = "rotate(180deg)";
  }

  return svg;
}
